import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { FiAlertTriangle, FiCheck, FiCopy, FiEye, FiEyeOff, FiKey, FiRotateCw, FiShield, FiSlash } from "react-icons/fi";

/**
 * Card for the System API access token — the time-limited "admin backdoor"
 * minted from this panel. Shows the current token's status, lets an admin
 * generate a new one (which atomically revokes the previous), reveals the
 * plaintext exactly once, and supports manual revocation.
 *
 * The plaintext is NEVER cached or persisted client-side beyond the modal
 * that displays it after generation; once dismissed it is gone for good.
 */
type ApiAccessTokenStatus = {
	active: boolean;
	prefix?: string | null;
	issuedBySub?: string | null;
	issuedByLabel?: string | null;
	issuedAt?: string | null;
	expiresAt?: string | null;
	secondsRemaining?: number | null;
	lastUsedAt?: string | null;
};

type GeneratedTokenResponse = {
	token: string;
	prefix: string;
	expiresAt: string;
	secondsRemaining: number;
};

const ENDPOINT = "/api/system/api-access-token";

export function SystemTokenCard(): React.ReactElement {
	const [status, setStatus] = useState<ApiAccessTokenStatus | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [generating, setGenerating] = useState(false);
	const [revoking, setRevoking] = useState(false);
	const [revealed, setRevealed] = useState<GeneratedTokenResponse | null>(null);
	const [showPlaintext, setShowPlaintext] = useState(false);
	const [copied, setCopied] = useState(false);
	const [now, setNow] = useState(() => Date.now());
	const tickRef = useRef<number | null>(null);

	const refresh = useCallback(async () => {
		try {
			const response = await axios.get<ApiAccessTokenStatus>(ENDPOINT);
			setStatus(response.data ?? null);
			setError(null);
		} catch (err: any) {
			setError(err?.response?.data?.message ?? err?.message ?? "Failed to load token status.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		refresh();
	}, [refresh]);

	useEffect(() => {
		// Drives the countdown timer; 1 Hz is enough.
		tickRef.current = window.setInterval(() => setNow(Date.now()), 1000);
		return () => {
			if (tickRef.current != null) window.clearInterval(tickRef.current);
		};
	}, []);

	const handleGenerate = useCallback(async () => {
		if (generating) return;
		setGenerating(true);
		setError(null);
		try {
			const response = await axios.post<GeneratedTokenResponse>(`${ENDPOINT}/generate`, {});
			setRevealed(response.data);
			setShowPlaintext(false);
			setCopied(false);
			await refresh();
		} catch (err: any) {
			setError(err?.response?.data?.message ?? err?.message ?? "Token generation failed.");
		} finally {
			setGenerating(false);
		}
	}, [generating, refresh]);

	const handleRevoke = useCallback(async () => {
		if (revoking) return;
		const ok = window.confirm(
			"Revoke the active system token now? Any automation using it will start receiving 401 immediately."
		);
		if (!ok) return;
		setRevoking(true);
		setError(null);
		try {
			await axios.post(`${ENDPOINT}/revoke`, {});
			await refresh();
		} catch (err: any) {
			setError(err?.response?.data?.message ?? err?.message ?? "Token revocation failed.");
		} finally {
			setRevoking(false);
		}
	}, [revoking, refresh]);

	const handleCopy = useCallback(async () => {
		if (!revealed) return;
		try {
			await navigator.clipboard.writeText(revealed.token);
			setCopied(true);
			window.setTimeout(() => setCopied(false), 2000);
		} catch {
			setError("Could not access the clipboard. Copy the value manually.");
		}
	}, [revealed]);

	const handleDismissReveal = useCallback(() => {
		setRevealed(null);
		setShowPlaintext(false);
		setCopied(false);
	}, []);

	const liveCountdown = useMemo(() => {
		if (!status?.active || !status.expiresAt) return null;
		const expiresAtMs = new Date(status.expiresAt).getTime();
		const remainingSec = Math.max(0, Math.floor((expiresAtMs - now) / 1000));
		return formatDuration(remainingSec);
	}, [now, status]);

	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
			<div className="flex items-center justify-between gap-4">
				<div>
					<h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">System API token</h2>
					<p className="mt-1 text-xs text-slate-500">
						Time-limited admin token for automations and CI. Defaults to a 1-hour lifetime, revokes the previous on each new generation.
					</p>
				</div>
				<FiShield className="h-5 w-5 shrink-0 text-slate-400" />
			</div>

			{loading && (
				<div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm text-slate-500">
					Loading token status…
				</div>
			)}

			{!loading && status && !status.active && (
				<div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
					No active system token. Generate one below to authenticate automated calls against the admin API.
				</div>
			)}

			{!loading && status && status.active && (
				<div className="mt-4 space-y-3 rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3">
					<div className="flex items-center justify-between gap-3">
						<div className="min-w-0">
							<p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Active</p>
							<p className="mt-0.5 truncate text-sm font-mono text-slate-800">{status.prefix}…</p>
						</div>
						<div className="text-right">
							<p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Expires in</p>
							<p className="mt-0.5 text-sm font-medium text-slate-800">{liveCountdown}</p>
						</div>
					</div>
					<dl className="grid grid-cols-1 gap-2 text-xs text-slate-600 sm:grid-cols-2">
						<div>
							<dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Issued by</dt>
							<dd className="truncate">{status.issuedByLabel ?? status.issuedBySub ?? "—"}</dd>
						</div>
						<div>
							<dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Issued at</dt>
							<dd>{formatDateTime(status.issuedAt)}</dd>
						</div>
						<div>
							<dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Expires at</dt>
							<dd>{formatDateTime(status.expiresAt)}</dd>
						</div>
						<div>
							<dt className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Last used</dt>
							<dd>{formatDateTime(status.lastUsedAt) ?? "Never"}</dd>
						</div>
					</dl>
				</div>
			)}

			{error && (
				<div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
					<FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
					<span>{error}</span>
				</div>
			)}

			<div className="mt-6 grid gap-3 sm:grid-cols-2">
				<button
					type="button"
					onClick={handleGenerate}
					disabled={generating}
					className="flex items-center justify-center gap-2 rounded-xl border border-slate-900 bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{generating ? <FiRotateCw className="h-4 w-4 animate-spin" /> : <FiKey className="h-4 w-4" />}
					<span>{status?.active ? "Regenerate token" : "Generate token"}</span>
				</button>
				<button
					type="button"
					onClick={handleRevoke}
					disabled={revoking || !status?.active}
					className="flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{revoking ? <FiRotateCw className="h-4 w-4 animate-spin" /> : <FiSlash className="h-4 w-4" />}
					<span>Revoke</span>
				</button>
			</div>

			<p className="mt-4 text-[11px] leading-relaxed text-slate-500">
				The plaintext is shown once — copy it immediately. It cannot be recovered later. Generating a new token revokes the previous one.
			</p>

			{revealed && (
				<div className="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-900/60 px-4">
					<div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
						<div className="flex items-start justify-between gap-3">
							<div>
								<h3 className="text-base font-semibold text-slate-900">New system token</h3>
								<p className="mt-1 text-xs text-slate-500">
									Copy this value now — it will not be shown again. Expires {formatDateTime(revealed.expiresAt)}.
								</p>
							</div>
							<FiKey className="h-5 w-5 text-slate-400" />
						</div>

						<div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
							<div className="flex items-center justify-between gap-2">
								<code className="block min-w-0 flex-1 truncate font-mono text-sm text-slate-800">
									{showPlaintext ? revealed.token : maskToken(revealed.token)}
								</code>
								<div className="flex shrink-0 items-center gap-1">
									<button
										type="button"
										onClick={() => setShowPlaintext((v) => !v)}
										className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-600 hover:border-slate-300"
										aria-label={showPlaintext ? "Hide token" : "Reveal token"}
									>
										{showPlaintext ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
									</button>
									<button
										type="button"
										onClick={handleCopy}
										className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-slate-600 hover:border-slate-300"
										aria-label="Copy token"
									>
										{copied ? <FiCheck className="h-4 w-4 text-emerald-600" /> : <FiCopy className="h-4 w-4" />}
									</button>
								</div>
							</div>
						</div>

						<div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
							<FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
							<span>
								Treat this token like a password. Anyone holding it can call the admin API as you for the next hour.
							</span>
						</div>

						<div className="mt-5 flex justify-end">
							<button
								type="button"
								onClick={handleDismissReveal}
								className="rounded-xl border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
							>
								I have saved the token
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

function formatDuration(seconds: number): string {
	if (seconds <= 0) return "expired";
	const h = Math.floor(seconds / 3600);
	const m = Math.floor((seconds % 3600) / 60);
	const s = seconds % 60;
	const pad = (n: number) => n.toString().padStart(2, "0");
	if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
	if (m > 0) return `${m}m ${pad(s)}s`;
	return `${s}s`;
}

function formatDateTime(value: string | null | undefined): string | null {
	if (!value) return null;
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleString();
}

function maskToken(token: string): string {
	// Keep the recognisable prefix, hide the secret portion.
	if (token.length <= 12) return "•".repeat(token.length);
	return token.slice(0, 12) + "•".repeat(token.length - 12);
}
